import React from "react";

/**
 * CardSkeleton - Loading skeleton for article/blog cards
 */
export const CardSkeleton = () => (
  <div className="animate-pulse">
    <div className="glass-strong rounded-2xl overflow-hidden shadow-apple">
      {/* Image skeleton */}
      <div className="h-44 bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-shimmer" />
      </div>

      {/* Content skeleton */}
      <div className="p-5">
        {/* Category badge */}
        <div className="mb-3 inline-block">
          <div className="h-5 w-16 bg-slate-200 rounded-full" />
        </div>

        {/* Title */}
        <div className="space-y-2 mb-4">
          <div className="h-5 bg-slate-200 rounded w-3/4" />
          <div className="h-5 bg-slate-200 rounded w-2/3" />
        </div>

        {/* Excerpt */}
        <div className="space-y-2 mb-4">
          <div className="h-4 bg-slate-100 rounded w-full" />
          <div className="h-4 bg-slate-100 rounded w-5/6" />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between">
          <div className="h-3 w-16 bg-slate-100 rounded" />
          <div className="h-3 w-20 bg-slate-100 rounded" />
        </div>
      </div>
    </div>
  </div>
);

/**
 * PricingCardSkeleton - Loading skeleton for pricing plan cards
 */
export const PricingCardSkeleton = () => (
  <div className="animate-pulse">
    <div className="glass-strong rounded-2xl p-8 shadow-apple h-96">
      {/* Badge */}
      <div className="inline-block mb-4 h-6 w-24 bg-slate-200 rounded-full" />

      {/* Title */}
      <div className="h-8 bg-slate-200 rounded w-2/3 mb-3" />

      {/* Description */}
      <div className="space-y-2 mb-6">
        <div className="h-4 bg-slate-100 rounded w-full" />
        <div className="h-4 bg-slate-100 rounded w-5/6" />
      </div>

      {/* Price */}
      <div className="mb-6">
        <div className="h-10 bg-slate-200 rounded w-1/3 mb-2" />
        <div className="h-4 bg-slate-100 rounded w-1/2" />
      </div>

      {/* Features list */}
      <div className="space-y-3 mb-8">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex gap-3">
            <div className="h-4 w-4 bg-slate-200 rounded flex-shrink-0 mt-1" />
            <div className="h-4 bg-slate-100 rounded flex-1" />
          </div>
        ))}
      </div>

      {/* Button */}
      <div className="h-10 bg-slate-200 rounded-lg w-full" />
    </div>
  </div>
);

/**
 * TextSkeleton - Generic text loading skeleton
 */
export const TextSkeleton = ({ lines = 3, className = "" }) => (
  <div className={`animate-pulse space-y-2 ${className}`}>
    {Array(lines)
      .fill(0)
      .map((_, i) => (
        <div
          key={i}
          className={`h-4 bg-slate-200 rounded ${
            i === lines - 1 ? "w-4/5" : "w-full"
          }`}
        />
      ))}
  </div>
);

/**
 * AvatarSkeleton - Loading skeleton for user avatars
 */
export const AvatarSkeleton = ({ size = "md" }) => {
  const sizeClasses = {
    sm: "w-8 h-8",
    md: "w-12 h-12",
    lg: "w-16 h-16",
  };

  return (
    <div
      className={`${sizeClasses[size]} bg-gradient-to-br from-slate-200 to-slate-100 rounded-full animate-pulse`}
    />
  );
};
